import { CommentInput } from "@/components/comments/CommentInput.tsx";
import { emojiDefaults } from "@/components/emoji/emoji.type.ts";
import {
  useModals,
  useToasts,
  Modal,
  ModalProps,
  Box,
  Button,
  Radio,
  Stack,
} from "@skailer-com/uikit";
import { Search } from "@/components/search/Search.tsx";
import { useMentionsHook } from "@/hooks/use-mention.hook.ts";
import { useSearchableList } from "@/hooks/use-searchable-list.hook.ts";
import {
  useCandidateActionCreate,
  useCandidateActionUpdate,
  useCandidateActionUpdateByFilter,
} from "@/queries/recruit/use-candidate-actions.query.ts";
import { useDisqualifications } from "@/queries/recruit/use-disqualifications.query.ts";
import { usePipelineById } from "@/queries/recruit/use-pipelines.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiAction,
  ApiActionCreateByFilterPayload,
  ApiActionPayload,
  ApiActionVisibilities,
} from "@/types/action.types.ts";
import { ApiFile } from "@/types/common.types.ts";
import { ApiCandidate } from "@/types/recruit/applicant.types.ts";
import { ApiCandidateViaApplicantFilter } from "@/types/recruit/candidate.types.ts";
import { ApiStageInPipeline } from "@/types/recruit/pipeline.types.ts";
import { getStageEmoji } from "@/types/recruit/stages.types.ts";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ChangeStageModalProps extends ModalProps {
  pipelineId: number;

  action?: ApiAction;
  candidate?: ApiCandidate;
  applicantFilter?: ApiCandidateViaApplicantFilter;

  onUpdatedByFilter?: () => void;
  secondButtonTitle?: string;
}

export function ChangeStageModal({
  pipelineId,
  candidate,
  action,
  applicantFilter,
  onUpdatedByFilter,
  secondButtonTitle,
  ...props
}: ChangeStageModalProps) {
  const { close } = useModals();
  const { t } = useTranslation();
  const { id } = { ...props };
  const { data: pipeline } = usePipelineById(pipelineId);
  const { data: disqualificationReasons } = useDisqualifications();

  const { showError, showInfo } = useToasts();
  const { replaceMentions } = useMentionsHook();

  const [comment, setComment] = useState(
    replaceMentions(action?.comment_data?.text, action?.comment_data?.mentioned_employees) || ""
  );
  const [files, setFiles] = useState<ApiFile[]>(action?.comment_data?.files || []);

  const [actionDate, setActionDate] = useState<Date>(action?.datetime_action_date ?? new Date());

  const handleChangeActionDate = (date: Date | null) => {
    if (!date) return;
    // Создаем UTC дату с учетом того что бы бэк ее правильно перевл в UTC
    const clearedDate = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
    );
    setActionDate(clearedDate);
  };

  const {
    shouldShowSearch: shouldShowStagesSearch,
    searchTerm: stagesSearchTerm,
    setSearchTerm: setStagesSearchTerm,
    filteredItems: filteredStages,
  } = useSearchableList(pipeline?.stages);

  const {
    shouldShowSearch: shouldShowDisqualificationSearch,
    searchTerm: disqualificationSearchTerm,
    setSearchTerm: setDisqualificationSearchTerm,
    filteredItems: filteredDisqualificationReasons,
  } = useSearchableList(disqualificationReasons);

  const [selectedStageId, setSelectedStageId] = useState<string | undefined>(() => {
    if (action) {
      if (action.pipeline_stage) return action.pipeline_stage.id.toString();
      return undefined;
    }

    if (candidate) {
      if (candidate.pipeline_stage) return candidate.pipeline_stage.id.toString();
    }

    return undefined;
  });

  const [isDeclineMode, setIsDeclineMode] = useState<boolean>(false);
  const [disqualificationReasonId, setDisqualificationReasonId] = useState<string | undefined>();

  const [syncedDisqualificationReasons, setSyncedDisqualificationReasons] = useState<
    typeof disqualificationReasons | null
  >(null);
  if (disqualificationReasons !== syncedDisqualificationReasons) {
    setSyncedDisqualificationReasons(disqualificationReasons);
    // Причина из action/candidate может быть архивной — сетим id, только если она есть в списке.
    if (action && action.disqualification_reason) {
      setIsDeclineMode(true);
      const action_reason = action.disqualification_reason;
      if (disqualificationReasons?.some((reason) => reason.id === action_reason.id)) {
        setDisqualificationReasonId(action_reason.id.toString());
      }
    }
    if (candidate && candidate.disqualification_reason) {
      setIsDeclineMode(true);
      const candidate_reason = candidate.disqualification_reason;
      if (disqualificationReasons?.some((reason) => reason.id === candidate_reason.id)) {
        setDisqualificationReasonId(candidate_reason.id.toString());
      }
    }
  }

  const candidateActionCreate = useCandidateActionCreate();
  const candidateActionUpdate = useCandidateActionUpdate();
  const candidateActionUpdateByFilter = useCandidateActionUpdateByFilter();

  const save = useCallback(
    (reserved?: boolean) => {
      const payload: ApiActionPayload = {
        visibility: ApiActionVisibilities.public,
        comment: comment,
        file_uuids: files.map((file) => file.uuid),
        datetime_action_date: actionDate,
      };
      let pipelineStage: ApiStageInPipeline | undefined = undefined;
      if (isDeclineMode) {
        if (!disqualificationReasonId)
          return showError({ title: t("ats:choose_disqualification_reason") });
        payload.disqualification_reason_id = +disqualificationReasonId;
        payload.reserved = reserved || false;
      } else {
        if (!selectedStageId) return showError({ title: t("ats:choose_stage") });
        pipelineStage = pipeline?.stages.find((stage) => stage.id == +selectedStageId);
        if (!pipelineStage) return showError({ title: t("ats:choose_stage") });

        payload.pipeline_stage_id = pipelineStage.id;
      }

      if (action) {
        candidateActionUpdate.mutate(
          { id: action.item_id, actionId: action.id, payload: payload },
          {
            onSuccess: () => {
              analyticsService.trackEvent(analyticEvents.candidates.actionEdited, {
                [analyticProperties.id]: action.item_id,
              });
              close(id);
            },
          }
        );
      } else if (candidate) {
        candidateActionCreate.mutate(
          { id: candidate.id, payload: payload },
          {
            onSuccess: () => {
              if (isDeclineMode) {
                analyticsService.trackEvent(analyticEvents.candidates.disqualified, {
                  [analyticProperties.id]: candidate.id,
                  [analyticProperties.count]: 1,
                  [analyticProperties.reserved]: reserved || false,
                });
              } else {
                if (candidate.disqualification_reason) {
                  analyticsService.trackEvent(analyticEvents.candidates.qualified, {
                    [analyticProperties.id]: candidate.id,
                    [analyticProperties.pipelineStageTitle]: pipelineStage?.title,
                  });
                } else {
                  analyticsService.trackEvent(analyticEvents.candidates.changedStage, {
                    [analyticProperties.id]: candidate.id,
                    [analyticProperties.pipelineStageTitle]: pipelineStage?.title,
                    [analyticProperties.count]: 1,
                  });
                }
              }
              close(id);
            },
          }
        );
      } else if (applicantFilter) {
        const updateByFilterPayload: ApiActionCreateByFilterPayload = {
          filter: applicantFilter,
          action: payload,
        };

        candidateActionUpdateByFilter.mutate(updateByFilterPayload, {
          onSuccess: () => {
            if (isDeclineMode) {
              analyticsService.trackEvent(analyticEvents.candidates.disqualified, {
                [analyticProperties.count]: applicantFilter.analyticsTotalUpdatedCount,
                [analyticProperties.reserved]: payload.reserved,
              });
            } else {
              analyticsService.trackEvent(analyticEvents.candidates.changedStage, {
                [analyticProperties.count]: applicantFilter.analyticsTotalUpdatedCount,
                [analyticProperties.pipelineStageTitle]: pipelineStage?.title,
              });
            }
            showInfo({ title: t("common:you_changes_were_saved") });
            onUpdatedByFilter?.();
            close(id);
          },
        });
      }
    },
    [isDeclineMode, pipeline, selectedStageId, disqualificationReasonId, comment, files, actionDate]
  );

  const onNext = useCallback(
    (reserve?: boolean) => {
      save(reserve || false);
    },
    [isDeclineMode, save]
  );

  const onBack = useCallback(() => {
    if (!isDeclineMode) return undefined;

    return () => {
      setIsDeclineMode(false);
      setSelectedStageId(undefined);
    };
  }, [isDeclineMode, save]);

  return (
    <Modal
      {...props}
      title={isDeclineMode ? t("ats:choose_disqualification_reason") : t("ats:choose_stage")}
      withCloser={true}
      variant={"side"}
      layout={"stretch"}
      onBack={onBack()}
      actions={
        <Stack gap={"sm"}>
          <Button
            type={"submit"}
            isLoading={candidateActionCreate.isPending || candidateActionUpdateByFilter.isPending}
            onClick={() => {
              onNext();
            }}
            size={"lg"}
            className={"w-full"}
          >
            {isDeclineMode ? t("ats:disqualify") : t("common:save")}
          </Button>
          {isDeclineMode && (
            <Button
              type={"submit"}
              isLoading={candidateActionCreate.isPending || candidateActionUpdateByFilter.isPending}
              onClick={() => {
                onNext(true);
              }}
              variant={"plain"}
              size={"lg"}
              className={"w-full"}
            >
              {t("ats:disqualify_and_reserve")}
            </Button>
          )}
          {secondButtonTitle && (
            <Button
              onClick={() => {
                close(id);
              }}
              variant={"plain"}
              size={"lg"}
              className={"w-full"}
            >
              {secondButtonTitle}
            </Button>
          )}
        </Stack>
      }
    >
      <form>
        <Stack direction={"vertical"}>
          {!isDeclineMode && (
            <>
              {shouldShowStagesSearch && (
                <Search
                  autoFocus
                  placeholder={t("common:search")}
                  value={stagesSearchTerm}
                  onInput={(e) => setStagesSearchTerm(e.currentTarget.value)}
                  type="text"
                />
              )}

              <Radio
                key={`option-disqualification`}
                onClick={(event) => {
                  setIsDeclineMode(true);
                  event.stopPropagation();
                }}
                className={"py-4 border-b border-dark/10"}
                emoji={emojiDefaults.stages.disqualified}
                title={t("ats:disqualify")}
              />
              {filteredStages.map((stage) => (
                <Box key={`option-stage-${stage.id}}`} className={"py-2 border-b border-dark/10"}>
                  <Radio
                    value={stage.id.toString()}
                    checked={stage.id.toString() == selectedStageId}
                    onChange={() => {
                      setSelectedStageId(stage.id.toString());
                    }}
                    emoji={getStageEmoji(stage)}
                    title={stage.title}
                  />
                  {stage.id.toString() == selectedStageId && (
                    <CommentInput
                      className={"pl-8"}
                      comment={comment}
                      files={files}
                      date={actionDate}
                      onChangeComment={(text) => setComment(text)}
                      onChangeFiles={(files) => setFiles(files)}
                      onChangeDate={handleChangeActionDate}
                    />
                  )}
                </Box>
              ))}
            </>
          )}
          {isDeclineMode && (
            <Stack gap={"md"} direction={"vertical"}>
              {shouldShowDisqualificationSearch && (
                <Search
                  placeholder={t("common:search")}
                  value={disqualificationSearchTerm}
                  onInput={(e) => setDisqualificationSearchTerm(e.currentTarget.value)}
                  type="text"
                  autoFocus
                />
              )}
              <Stack direction={"vertical"} gap={"sm"}>
                {filteredDisqualificationReasons.map((disqualificationReason) => (
                  <Box
                    key={`disqualification-reason-${disqualificationReason.id}`}
                    className={"py-2 border-b border-dark/10"}
                  >
                    <Radio
                      value={disqualificationReason.id.toString()}
                      checked={disqualificationReason.id.toString() == disqualificationReasonId}
                      onChange={() => {
                        setDisqualificationReasonId(disqualificationReason.id.toString());
                      }}
                      title={disqualificationReason.title}
                    />
                    {disqualificationReason.id.toString() == disqualificationReasonId && (
                      <CommentInput
                        className={"pl-8"}
                        comment={comment}
                        files={files}
                        date={actionDate}
                        onChangeComment={(text) => setComment(text)}
                        onChangeFiles={(files) => setFiles(files)}
                        onChangeDate={handleChangeActionDate}
                      />
                    )}
                  </Box>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>
      </form>
    </Modal>
  );
}
